import { useEffect } from 'react';

const SITE_URL = 'https://dai.lids.mit.edu';
const SITE_NAME = 'DAI Group';
const ORG_ID = `${SITE_URL}/#organization`;
const PAGE_JSONLD_ID = 'page-jsonld';

function absoluteUrl(path) {
  if (!path || path === '/') return `${SITE_URL}/`;
  return `${SITE_URL}${path}`;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertPageJsonLd(json) {
  let el = document.getElementById(PAGE_JSONLD_ID);
  if (!json) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = PAGE_JSONLD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
}

/**
 * Keeps per-route <head> metadata in sync with the current page: title,
 * description, canonical URL, Open Graph + Twitter fields, and — for deep
 * pages — a WebPage + BreadcrumbList JSON-LD block. The homepage skips the
 * JSON-LD because public/index.html already ships the full entity graph.
 *
 * Note: og:* tags use property=, twitter:* tags use name= (matching index.html).
 *
 * @param {Object}  opts
 * @param {string}  opts.title          Document title.
 * @param {string}  opts.description    Meta description.
 * @param {string} [opts.path='/']      Route path (e.g. '/projects/sdv').
 * @param {string} [opts.breadcrumbName] Human label for this page in breadcrumbs.
 */
export default function usePageMeta({ title, description, path = '/', breadcrumbName }) {
  useEffect(() => {
    const url = absoluteUrl(path);

    if (title) document.title = title;
    upsertLink('canonical', url);
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    if (path && path !== '/') {
      upsertPageJsonLd({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${url}#webpage`,
            url,
            name: title,
            description,
            isPartOf: { '@id': `${SITE_URL}/#website` },
            about: { '@id': ORG_ID },
            inLanguage: 'en',
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${url}#breadcrumb`,
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: SITE_NAME, item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: breadcrumbName || title, item: url },
            ],
          },
        ],
      });
    } else {
      upsertPageJsonLd(null);
    }
  }, [title, description, path, breadcrumbName]);
}
